import { useState } from "react";

import { useResetPassword } from "@/hooks/use-auth-client";
import useUpdateEffect from "@/hooks/use-update-effect";

import { PATHS } from "@/routes/paths";

import { Button } from "@/components/ui/button/button";
import { InputPasswordField } from "@/components/ui/form/fields/input-field/input-password";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";

import { useLoadingScreen } from "../../side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";
import { AuthForm } from "../_components/auth-form";
import { useNewPasswordError } from "../_shared/use-new-password-error";
import { authResetPasswordInputZodSchema, type AuthResetPasswordInputType } from "./_schema/validation-schema";

import type { SafeParseError } from "zod";

type Props = {
	email: string;
	verifyCode: string;
};

export const AuthFormResetPassword = ({ email, verifyCode }: Props) => {
	const [password, setPassword] = useState("");

	const [validationErrors, setValidationErrors] = useState<SafeParseError<AuthResetPasswordInputType> | undefined>(
		undefined,
	);

	const newPassword = useNewPasswordError({
		password,
		email,
		formatErrorMessage: validationErrors?.error.formErrors.fieldErrors.password?.at(0),
	});

	const canSubmit = email && password && !validationErrors && newPassword.isSubmittable;

	useUpdateEffect(() => {
		const zodErrors = authResetPasswordInputZodSchema.safeParse({ password });

		zodErrors.success ? setValidationErrors(undefined) : setValidationErrors(zodErrors);
	}, [password]);

	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { resetPassword } = useResetPassword();
	const handleSubmit = (_e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		showLoadingScreenCallback();

		resetPassword({ email, password, confirmationCode: verifyCode }).then(() => {
			hideLoadingScreenCallback();
		});
	};

	return (
		<AuthForm
			formTitle="パスワードの再設定"
			primaryButtonText="パスワードを変更"
			handlePrimaryButtonClick={handleSubmit}
			disabled={!canSubmit}
			otherInfo={
				<div className="flex justify-end">
					<a href={PATHS.AUTH.FORGOT_PASSWORD}>
						<Button variant="link">パスワードを忘れた場合</Button>
					</a>
				</div>
			}
		>
			<InputTextField
				label="メールアドレス"
				autoComplete="email"
				name="email"
				errorMessage={undefined}
				type="email"
				placeholder="sample@chillnn.com"
				value={email}
				handleChange={() => {}}
				readOnly
			/>

			<InputPasswordField
				label="パスワード"
				autoComplete="new-password"
				name="password"
				errorMessage={newPassword.errorMessage}
				placeholder="••••••••"
				value={password}
				onChange={setPassword}
			/>
		</AuthForm>
	);
};
